import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import {useAuth} from './AuthContext';
import {can} from '../lib/permissions';
import {identityScope} from '../lib/sessionScope';

const RefDataContext = createContext(null);

export function RefDataProvider({ children }) {
  const auth = useAuth();
  const scope = auth?.sessionKey || identityScope(auth?.user);
  const [result, setResult] = useState(null);
  const generation = useRef(0), pending = useRef(null);
  const invalidate = useCallback(() => {generation.current++;pending.current?.abort();}, []);

  const reload = useCallback(async () => {
    const current = ++generation.current;
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    const results = await Promise.allSettled([
      can(auth?.user,"vehicles.read") ? api.get("/vehicles", {signal:controller.signal}) : Promise.resolve({data:[]}),
      can(auth?.user,"suppliers.read") ? api.get("/suppliers", {signal:controller.signal}) : Promise.resolve({data:[]}),
      Promise.resolve({data:auth?.user?.sites || []}),
      can(auth?.user,"rentals.read") ? api.get("/assignees", {signal:controller.signal}) : Promise.resolve({data:[]}),
    ]);
    if (controller.signal.aborted || current !== generation.current) return;
    const values = results.map(item => item.status === 'fulfilled' && Array.isArray(item.value.data) ? item.value.data : []);
    setResult({scope, vehicles:values[0], suppliers:values[1], locations:values[2], assignees:values[3]});
  // scope includes the authenticated site and access revision.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  useEffect(() => { reload(); return invalidate; }, [reload, invalidate]);
  const {vehicles, suppliers, locations, assignees} = result?.scope === scope ? result : {vehicles:[],suppliers:[],locations:[],assignees:[]};

  const nameFor = (list, id, keys = ["name"]) => {
    const item = list.find((x) => x.id === id);
    if (!item) return "—";
    return keys.map((k) => item[k]).filter(Boolean).join(" ");
  };

  return (
    <RefDataContext.Provider value={{ vehicles, suppliers, locations, assignees, reload, nameFor }}>
      {children}
    </RefDataContext.Provider>
  );
}

export const useRefData = () => useContext(RefDataContext);
